// Bileli · modules/account/viewmodel — state + logic for the profile page; ProfilePanel stays pure.
// Owns: /me/stats fetch (+ client-derived level), display-name save (renames the personal org — same
// publish-gate path as the editor), the Telegram-first avatar sync, the Bileli theme/language switches,
// and Telegram-settings hand-offs.

import { useEffect, useState } from '../../../../lib/teact/teact';
import { getActions } from '../../../../global';

import type { BileliLang } from '../../../shared/i18n/langStore';
import type { BileliTheme } from '../../../shared/lib/themeStore';
import type { LevelInfo, MyStats } from '../model/stats.api';
import { SettingsScreens } from '../../../../types';

import { t } from '../../../shared/i18n';
import { bileliLangStore, setBileliLang } from '../../../shared/i18n/langStore';
import { openTelegramSettings } from '../../../shared/lib/openTelegramSettings';
import { bileliThemeStore, setBileliTheme } from '../../../shared/lib/themeStore';
import { useStoreState } from '../../../shared/lib/zustandTeact';
import { hasTelegramPhoto, setMyAvatar, syncTelegramAvatar } from '../model/avatar.api';
import { isUsableProfileName, setSessionUser, updateMyProfile } from '../model/profile.api';
import { fetchMyStats, levelOf } from '../model/stats.api';

import useAsync from '../../../../hooks/useAsync';
import useLastCallback from '../../../../hooks/useLastCallback';

// Auto-sync at most once per session (mirrors the UsernameNudge single-flight): when the Bileli avatar is
// empty but Telegram has a photo, adopt it silently on the first profile open.
let autoSyncAttempted = false;

export interface ProfileVm {
  stats?: MyStats;
  level?: LevelInfo;
  saveName: (name: string) => Promise<void>;
  isSavingName: boolean;
  nameError?: string;
  /** Telegram-first avatar: sync when Telegram has a photo, hand off to Telegram settings when not. */
  telegramHasPhoto: boolean;
  isSyncingAvatar: boolean;
  avatarError?: string;
  syncAvatar: () => void;
  removeAvatar: () => void;
  theme: BileliTheme;
  setTheme: (t: BileliTheme) => void;
  lang: BileliLang;
  setLang: (l: BileliLang) => void;
  openTelegramProfile: () => void;
  openTelegramLanguage: () => void;
}

export function useProfile(hasBileliAvatar: boolean): ProfileVm {
  const theme = useStoreState(bileliThemeStore, (s) => s.theme);
  const lang = useStoreState(bileliLangStore, (s) => s.lang);
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [isSyncingAvatar, setIsSyncingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | undefined>(undefined);
  const telegramHasPhoto = hasTelegramPhoto();

  const { result: stats } = useAsync(() => fetchMyStats(), []);

  const syncAvatar = useLastCallback(() => {
    if (isSyncingAvatar) return;
    setIsSyncingAvatar(true);
    setAvatarError(undefined);
    syncTelegramAvatar()
      .then(setSessionUser) // avatarUrl lands in the session → header + profile re-render
      .catch(() => setAvatarError(t('Не удалось получить фото из Telegram.')))
      .finally(() => setIsSyncingAvatar(false));
  });

  const removeAvatar = useLastCallback(() => {
    if (isSyncingAvatar) return;
    setIsSyncingAvatar(true);
    setMyAvatar(null)
      .then(setSessionUser)
      .catch(() => setAvatarError(t('Не удалось удалить фото.')))
      .finally(() => setIsSyncingAvatar(false));
  });

  // Silent one-time adoption of the Telegram photo when Bileli has none (Telegram-first, like the
  // username auto-resync). Runs once per session — a failed attempt won't loop.
  useEffect(() => {
    if (autoSyncAttempted || hasBileliAvatar || !telegramHasPhoto) return;
    autoSyncAttempted = true;
    syncAvatar();
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, []);

  const saveName = useLastCallback(async (raw: string) => {
    const name = raw.trim();
    if (!isUsableProfileName(name)) {
      setNameError(t('Введите настоящее имя (не только эмодзи).'));
      return;
    }
    setNameError(undefined);
    setIsSavingName(true);
    try {
      const updated = await updateMyProfile(name);
      setSessionUser(updated); // the header greeting/avatar initial reads the session user
    } catch (e) {
      setNameError(e instanceof Error ? e.message : t('Не удалось сохранить имя.'));
    } finally {
      setIsSavingName(false);
    }
  });

  const setTheme = useLastCallback((next: BileliTheme) => {
    setBileliTheme(next);
    // Keep Telegram chrome in step — a light Bileli over a dark chat (or vice versa) looks broken.
    // Pin the explicit choice (same pattern as the stock dark-mode toggle in LeftSideMenuItems).
    const { setSharedSettingOption } = getActions();
    setSharedSettingOption({ theme: next });
    setSharedSettingOption({ shouldUseSystemTheme: false });
  });

  return {
    stats,
    level: stats ? levelOf(stats.totalXp) : undefined,
    saveName,
    isSavingName,
    nameError,
    telegramHasPhoto,
    isSyncingAvatar,
    avatarError,
    syncAvatar,
    removeAvatar,
    theme,
    setTheme,
    lang,
    setLang: setBileliLang,
    openTelegramProfile: () => openTelegramSettings(SettingsScreens.EditProfile),
    openTelegramLanguage: () => openTelegramSettings(SettingsScreens.Language),
  };
}
